import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import { Box, Button, Chip, Paper, Stack, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import dayjs from 'dayjs';
import {
	ExtractionLogCell,
	ExtractionLogRow,
	listPendingMessages,
	StatusFilter,
} from '../../services/messageQueue.service';
import { ResolveExtractionLogDialog } from '../../components/Dialog/ResolveExtractionLogDialog';

const PAGE_SIZE = 20;

const unique = (values: Array<string | null | undefined>): string[] =>
	Array.from(new Set(values.map((value) => value?.trim()).filter((value): value is string => !!value)));

const getWorkers = (row: ExtractionLogRow): string => {
	const names: string[] = [];
	for (const cell of row.cells) {
		if (cell.participants?.length) {
			names.push(...cell.participants.map((participant) => participant.name).filter((name): name is string => !!name));
		} else {
			if (cell.labels.senderName) names.push(cell.labels.senderName);
			names.push(...(cell.coWorkers ?? []).map((worker) => worker.name));
		}
	}
	const result = unique(names);
	return result.length > 0 ? result.join(', ') : row.senderName ?? row.fromPhone ?? 'Unknown';
};

const getProjects = (row: ExtractionLogRow): string => {
	const projects = unique(row.cells.map((cell) => cell.labels.projectName));
	return projects.length > 0 ? projects.join(', ') : 'Needs selection';
};

const cellWorkLabel = (cell: ExtractionLogCell): string =>
	[cell.labels.typeName, cell.labels.subTypeName, cell.labels.subTypeValue].filter(Boolean).join(' · ');

const getWorkSummary = (row: ExtractionLogRow): string => {
	const work = unique(row.cells.map(cellWorkLabel));
	if (work.length === 0) return row.messageType === 'work-done' ? 'Needs selection' : 'Review message';
	const locationCount = row.cells.length;
	return `${work.join(', ')} · ${locationCount} location${locationCount === 1 ? '' : 's'}`;
};

const cellLocationLabel = (cell: ExtractionLogCell): string => {
	const hasStructuredLocation =
		cell.labels.blockNumber != null && cell.resolved.level.value != null && cell.resolved.unit.value != null;
	if (!hasStructuredLocation) return cell.reportedLocationText?.trim() || 'Needs review';
	return [
		cell.labels.precinctName,
		cell.labels.blockNumber ? `Blk ${cell.labels.blockNumber}` : null,
		cell.resolved.level.value != null ? `L${cell.resolved.level.value}` : null,
		cell.resolved.unit.value != null ? `U${cell.resolved.unit.value}` : null,
	]
		.filter(Boolean)
		.join(' · ');
};

const getLocationSummary = (row: ExtractionLogRow): string => {
	const locations = unique(row.cells.map(cellLocationLabel));
	if (locations.length === 0) return 'Needs review';
	if (locations.length <= 2) return locations.join(' | ');
	return `${locations.slice(0, 2).join(' | ')} +${locations.length - 2} more`;
};

const hasEvidence = (row: ExtractionLogRow): boolean => (row.evidence?.length ?? 0) > 0;

export function MessageQueue() {
	const [rows, setRows] = useState<ExtractionLogRow[]>([]);
	const [loading, setLoading] = useState(false);
	const [page, setPage] = useState(0);
	const [total, setTotal] = useState(0);
	const [selected, setSelected] = useState<ExtractionLogRow | null>(null);
	const [statusFilter, setStatusFilter] = useState<StatusFilter>('pending');

	const loadRows = useCallback(async (pageNumber: number, status: StatusFilter) => {
		try {
			setLoading(true);
			const result = await listPendingMessages(pageNumber, PAGE_SIZE, status, 'all');
			if (result.success) {
				setRows(result.data?.rows || []);
				setTotal(result.data?.totalItems || 0);
			} else {
				toast.error(result.message || 'Failed to load worker messages.');
			}
		} catch (err: any) {
			toast.error(err?.response?.data?.message || err?.message || 'Failed to load worker messages.');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		loadRows(page, statusFilter);
	}, [page, statusFilter, loadRows]);

	useEffect(() => {
		setPage(0);
	}, [statusFilter]);

	const columns: GridColDef[] = [
		{
			field: 'message',
			headerName: 'Message',
			minWidth: 260,
			flex: 1.35,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as ExtractionLogRow;
				const date = row.messageTimestamp || row.createdAt;
				return (
					<Box sx={{ minWidth: 0, width: '100%' }}>
						<Typography variant='body2' sx={{ fontWeight: 650, color: '#1e293b' }} noWrap>
							{row.rawMessage || 'No message text'}
						</Typography>
						<Typography variant='caption' color='text.secondary'>
							{date ? dayjs(date).format('DD MMM YYYY, HH:mm') : 'Unknown date'}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'assignment',
			headerName: 'Assignment',
			minWidth: 150,
			flex: 0.75,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as ExtractionLogRow;
				return (
					<Box sx={{ minWidth: 0, width: '100%' }}>
						<Typography variant='body2' sx={{ fontWeight: 650 }} noWrap>
							{getWorkers(row)}
						</Typography>
						<Typography variant='caption' color='text.secondary' noWrap>
							{getProjects(row)}
						</Typography>
					</Box>
				);
			},
		},
		{
			field: 'workSummary',
			headerName: 'Work',
			minWidth: 190,
			flex: 1,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as ExtractionLogRow;
				return (
					<Tooltip title={unique(row.cells.map(cellWorkLabel)).join(' | ')} arrow>
						<Typography variant='body2' sx={{ color: '#334155' }} noWrap>
							{getWorkSummary(row)}
						</Typography>
					</Tooltip>
				);
			},
		},
		{
			field: 'locationSummary',
			headerName: 'Location',
			minWidth: 165,
			flex: 0.9,
			sortable: false,
			valueGetter: (params) => getLocationSummary(params.row as ExtractionLogRow),
			renderCell: (params: GridRenderCellParams) => (
				<Tooltip title={unique(params.row.cells.map(cellLocationLabel)).join(' | ')} arrow>
					<Typography variant='body2' color='text.secondary' noWrap>
						{params.value as string}
					</Typography>
				</Tooltip>
			),
		},
		{
			field: 'status',
			headerName: 'Status',
			width: 168,
			sortable: false,
			renderCell: (params: GridRenderCellParams) => {
				const row = params.row as ExtractionLogRow;
				return (
					<Stack direction='row' alignItems='center' spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
						<Chip
							size='small'
							label={row.status === 'pending' ? 'Needs review' : 'Completed'}
							color={row.status === 'pending' ? 'warning' : 'success'}
							variant='outlined'
							sx={{ flexShrink: 0, fontWeight: 650, bgcolor: row.status === 'pending' ? '#fffaf0' : '#f2fbf5' }}
						/>
						{hasEvidence(row) && (
							<Tooltip title='Photo attached'>
								<ImageOutlinedIcon
									color='primary'
									fontSize='small'
									aria-label='Evidence attached'
									sx={{ flexShrink: 0 }}
								/>
							</Tooltip>
						)}
					</Stack>
				);
			},
		},
		{
			field: 'actions',
			headerName: '',
			width: 96,
			sortable: false,
			align: 'right',
			headerAlign: 'right',
			renderCell: (params: GridRenderCellParams) => (
				<Button
					size='small'
					variant={params.row.status === 'pending' ? 'contained' : 'outlined'}
					disableElevation
					onClick={(event) => {
						event.stopPropagation();
						setSelected(params.row as ExtractionLogRow);
					}}
					sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 700 }}>
					{params.row.status === 'pending' ? 'Review' : 'View'}
				</Button>
			),
		},
	];

	return (
		<Box sx={{ flex: 1, height: '100%', maxWidth: 1480, mx: 'auto', width: '100%' }}>
			<Helmet>
				<title>Worker Messages</title>
			</Helmet>
			<Stack
				direction={{ xs: 'column', sm: 'row' }}
				justifyContent='space-between'
				alignItems={{ sm: 'center' }}
				spacing={1.5}
				sx={{ mb: 2 }}>
				<Box>
					<Typography variant='h5' component='h2' sx={{ fontWeight: 750, letterSpacing: '-0.02em' }}>
						Worker Messages
					</Typography>
					<Typography variant='body2' color='text.secondary'>
						Review work reported through WhatsApp.
					</Typography>
				</Box>
				<ToggleButtonGroup
					value={statusFilter}
					exclusive
					size='small'
					onChange={(_, value) => value && setStatusFilter(value as StatusFilter)}
					sx={{
						p: 0.5,
						bgcolor: '#eef2f7',
						borderRadius: 2.5,
						'& .MuiToggleButton-root': {
							px: 2,
							py: 0.65,
							border: 0,
							borderRadius: '8px !important',
							textTransform: 'none',
							fontWeight: 700,
							color: '#64748b',
						},
						'& .Mui-selected': {
							bgcolor: '#fff !important',
							color: '#1d4ed8',
							boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)',
						},
					}}>
					<ToggleButton value='pending'>Needs review</ToggleButton>
					<ToggleButton value='done'>Completed</ToggleButton>
				</ToggleButtonGroup>
			</Stack>
			<Paper
				variant='outlined'
				sx={{
					height: 620,
					width: '100%',
					overflow: 'hidden',
					borderRadius: 3,
					borderColor: '#e2e8f0',
					boxShadow: '0 10px 30px rgba(15, 23, 42, 0.04)',
				}}>
				<DataGrid
					rows={rows}
					columns={columns}
					loading={loading}
					rowCount={total}
					page={page}
					pageSize={PAGE_SIZE}
					rowsPerPageOptions={[PAGE_SIZE]}
					pagination
					paginationMode='server'
					onPageChange={setPage}
					disableSelectionOnClick
					onRowClick={(params) => setSelected(params.row as ExtractionLogRow)}
					rowHeight={72}
					headerHeight={48}
					sx={{
						cursor: 'pointer',
						border: 0,
						color: '#334155',
						'& .MuiDataGrid-columnHeaders': {
							bgcolor: '#f8fafc',
							borderBottom: '1px solid #e2e8f0',
						},
						'& .MuiDataGrid-columnHeaderTitle': { fontWeight: 750, color: '#475569' },
						'& .MuiDataGrid-columnSeparator': { display: 'none' },
						'& .MuiDataGrid-cell': {
							px: 2,
							alignItems: 'center',
							borderBottom: '1px solid #edf1f5',
							outline: 'none !important',
						},
						'& .MuiDataGrid-row:hover': { bgcolor: '#f8fbff' },
						'& .MuiDataGrid-footerContainer': { borderTop: '1px solid #e2e8f0', minHeight: 54 },
						'& .MuiDataGrid-virtualScroller': { bgcolor: '#fff' },
					}}
					localeText={{
						noRowsLabel: statusFilter === 'pending' ? 'No messages need review.' : 'No completed messages.',
					}}
				/>
			</Paper>
			<ResolveExtractionLogDialog
				show={selected !== null}
				row={selected}
				readOnly={selected?.status === 'done'}
				onClose={() => setSelected(null)}
				onSuccess={() => {
					setSelected(null);
					loadRows(page, statusFilter);
				}}
				onEvidenceChanged={() => loadRows(page, statusFilter)}
			/>
		</Box>
	);
}
